import React from 'react';
import { RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import RoutableSitecoreLink from '../@shared/routableSitecoreLink';
import { TNextStepsFieldsCard, TNextStepsProps } from './types/nextSteps.types';
import { NextStepsModel } from './models/nextSteps.model';
import Icon from '../@shared/icon';
import { iconSize } from '../../constants/icon.constants';
import fields from '../../constants/fields.const';
import { FieldEditFrameComponent } from '../@shared/editFrame';
import './styles.scss';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { targetLinkdsk } from '../../utils/targetDskLink';
import useMediaQuery from '../../hooks/useMediaQuery';
import breakpoints from '../../constants/breakpoints.constants';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import {
  modelNextStepsClick,
  modelNextStepsVisible,
  seriesNextStepsClick,
  seriesNextStepsVisible,
} from '../../constants/analytics.constants';
import { googleNextStepsClick } from '../../utils/analytics';
import useBrandName from '../../hooks/useBrand.effect';
import useSitecoreContext from '../../hooks/useSitecoreContext';

const NextSteps = (props: TNextStepsProps) => {
  const isEEactive = props?.sitecoreContext?.pageEditing;
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const {sitecoreContext} = useSitecoreContext();
  const productLine = sitecoreContext?.analytics?.productLine;
  const productSeries = sitecoreContext?.analytics?.productSeries;
  const productModel = sitecoreContext?.analytics?.productModel;
  const visibleEvent = productModel ? modelNextStepsVisible : seriesNextStepsVisible;
  const clickEvent = productModel ? modelNextStepsClick : seriesNextStepsClick;
  const brandName = useBrandName();
  const model = new NextStepsModel(props, brandName);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  if (!isEEactive && !props?.rendering?.dataSource) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent event={visibleEvent}>
      <section className={'next-steps-section product-white-spacer'}>
        <div
          className={`next-steps-list__item--fill next-steps-list__item--fill__bg-${
            model.title?.value ? model.titleColor : model.ctaColor
          }`}
        />
        <div className={`next-steps-list next-steps-list__bg-${model.ctaColor}`}>
          {model.title?.value && (
            <RichText
              tag="div"
              className={`next-steps-list__item--title next-steps-list__item--title__bg-${model.titleColor} rich-text`}
              field={model.title}
            />
          )}
          {model?.cards?.map((item: TNextStepsFieldsCard, index: number) =>
            isEEactive ? (
              <div className={'next-steps-list__item-link next-steps-list__item-link--ee-active'}>
                <div className="next-steps-list__item-wrapper">
                  <div className={'next-steps-list__item'}>
                    <FieldEditFrameComponent itemId={item.id} fields={[fields.icon]} sitecoreContext={props?.sitecoreContext}>
                      <Icon
                        extraClassName="next-steps-list__item-icon"
                        iconField={item?.fields?.icon}
                        color={model.iconColor}
                        size={iconSize.xlg}
                      />
                    </FieldEditFrameComponent>
                  </div>
                </div>
                <RoutableSitecoreLink field={item.fields.url}></RoutableSitecoreLink>
              </div>
            ) : (
              <RoutableSitecoreLink
                field={item.fields.url}
                target={targetLinkdsk(item?.fields?.url, isDesktop)}
                className={'next-steps-list__item-link'}
                onClick={() =>
                  googleNextStepsClick(
                    clickEvent,
                    item.fields.url.value.title || item.fields.url.value.text,
                    item.fields.url.value.text,
                    item.fields.url.value.href,
                    productLine,
                    productSeries,
                    productModel
                  )
                }
              >
                <div {...(index !== model?.cards?.length - 1 && { className: 'next-steps-list__item-border-right' })}>
                  <div className="next-steps-list__item-wrapper">
                    <div className={'next-steps-list__item'}>
                      <Icon
                        extraClassName="next-steps-list__item-icon"
                        iconField={item?.fields?.icon}
                        color={model.iconColor}
                        size={iconSize.xlg}
                      />
                      {item.fields.url.value.text}
                    </div>
                  </div>
                </div>
              </RoutableSitecoreLink>
            )
          )}
        </div>
        <div
          className={`next-steps-list__item--fill next-steps-list__item--fill__bg-${
            model.cards?.length === 0 ? model.titleColor : model.ctaColor
          }`}
        />
      </section>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(NextSteps);
